import { DialpadIntegration } from "@/schema/dialpad-integration"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiContextFetch } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

interface AuthUriResponse {
  auth_uri: string
}

export const DIALPAD_INTEGRATIONS_QUERY_KEY = ["dialpad-integrations"]

export function useFetchDialpadIntegrations(enabled = true) {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useQuery<DialpadIntegration[]>({
    queryKey: [...DIALPAD_INTEGRATIONS_QUERY_KEY, bookId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      const data = await apiContextFetch<DialpadIntegration[]>(context, "dialpad_integrations")
      return data
    },
    enabled,
  })
}

export function useGetDialpadAuthUri() {
  const getContext = useApiContext()

  return useMutation<AuthUriResponse, Error, { returnUrl?: string; integrationGuid?: string } | void>({
    mutationKey: ["dialpad-auth-uri"],
    mutationFn: async (params = {}) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      const searchParams = new URLSearchParams()
      if (params && "returnUrl" in params && params.returnUrl) {
        searchParams.append("return_url", params.returnUrl)
      }
      if (params && "integrationGuid" in params && params.integrationGuid) {
        searchParams.append("integration_guid", params.integrationGuid)
      }

      const url = `dialpad_integrations/get_auth_uri${searchParams.toString() ? `?${searchParams.toString()}` : ""}`
      return apiContextFetch<AuthUriResponse>(context, url)
    },
  })
}

export function useSyncDialpadPhoneNumbers() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const bookId = useBookId()

  return useMutation<DialpadIntegration, Error, { id: string }>({
    mutationKey: ["dialpad-integration-sync-phone-numbers"],
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return apiContextFetch<DialpadIntegration>(context, `dialpad_integrations/${id}`, {
        method: "PUT",
        body: JSON.stringify({ sync_phone_numbers: true }),
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [...DIALPAD_INTEGRATIONS_QUERY_KEY, bookId] })
    },
  })
}

export function useDisconnectDialpadIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const bookId = useBookId()

  return useMutation<void, Error, { id: string }>({
    mutationKey: ["dialpad-integration-disconnect"],
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await apiContextFetch(context, `dialpad_integrations/${id}`, {
        method: "PUT",
        body: JSON.stringify({ disconnect: true }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...DIALPAD_INTEGRATIONS_QUERY_KEY, bookId] })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}

export function useDeleteDialpadIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const bookId = useBookId()

  return useMutation<void, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await apiContextFetch(context, `dialpad_integrations/${id}`, {
        method: "DELETE",
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...DIALPAD_INTEGRATIONS_QUERY_KEY, bookId] })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}
